import {createMultiStyleConfigHelpers, defineCssVars} from '@chakra-ui/styled-system';
import {useMultiStyleConfig, defineStyle} from '@chakra-ui/system';
import {mode} from '@chakra-ui/theme-tools';

import {semantic as semanticDark} from '../../../design-tokens/dark';
import {semantic} from '../../../design-tokens/light';

const parts = [`container`, `item`, `title`, `header`, `body`] as const;
type ChartTooltipParts = (typeof parts)[number];

const {defineMultiStyleConfig, definePartsStyle} = createMultiStyleConfigHelpers(parts);

const {shadow: $shadow} = defineCssVars(`chart-tooltip`, [`shadow`]);

const containerCssVars = {
  [$shadow.variable]: `shadows.xl`,
};

const container = defineStyle(props => {
  return {
    ...containerCssVars,
    backgroundColor: mode(
      semantic.colors.background[`elevation-3`],
      semanticDark.colors.background[`elevation-3`],
    )(props),
    width: `194px`,
    boxShadow: $shadow.reference,
    borderRadius: `radius-200`,
    padding: `space-200`,
    zIndex: 100,
  };
});

const title = defineStyle({
  fontWeight: `700`,
  fontSize: `md`,
  lineHeight: `24px`,
  pb: `space-150`,
});

const item = defineStyle({
  '&:not(:last-child)': {
    pb: `space-200`,
  },
});

const header = defineStyle(({colorScheme}) => {
  const accentLineStyles = colorScheme
    ? {
        '&::before': {
          borderRadius: `2px`,
          content: `""`,
          position: `absolute`,
          top: `4px`,
          left: 0,
          width: `4px`,
          height: `16px`,
          backgroundColor: colorScheme,
        },
      }
    : {};

  return {
    fontWeight: `700`,
    fontSize: `sm`,
    lineHeight: `24px`,
    position: `relative`,
    pl: `space-150`,
    ...accentLineStyles,
  };
});

const body = defineStyle({
  fontSize: `sm`,
  color: `text.secondary`,
  lineHeight: `24px`,
  pl: `space-150`,
});

const baseStyle = definePartsStyle(props => {
  return {
    container: container(props),
    title,
    item,
    header: header(props),
    body,
  };
});

export const ChartTooltipTheme = defineMultiStyleConfig({
  baseStyle,
});

export const useChartTooltipStyleConfig = <Part extends ChartTooltipParts>(
  part: Part,
  props?: Parameters<typeof useMultiStyleConfig>[1],
) => {
  const chartTooltip = useMultiStyleConfig(`ChartTooltip`, props ?? {});
  return chartTooltip?.[part];
};

export default ChartTooltipTheme;
